import React, { useCallback, useEffect, useRef, useState } from 'react';
import CodeEditor from './CodeEditor';
import ResizableSplitter from './ResizableSplitter';
import GraphQLSchemaExplorer from './GraphQLSchemaExplorer';
import {
  buildQueryFromSelectedPaths,
  parseQueryToSelectedPaths,
} from '../../utils/graphqlSchema';

const EMPTY_SELECTED_PATHS = new Set();

const GraphQLBodyEditor = ({
  body = {},
  onChange,
  schema = null,
  schemaStatus = 'idle',
  schemaError = null,
  splitLayoutTick = 0,
  selectedEnvironment,
  globalVariables,
  collectionVariables,
  folderVariables,
  workspaceVariables,
  onVariableUpdate,
  variableContext,
}) => {
  const query = typeof body?.query === 'string' ? body.query : '';
  const variables = typeof body?.variables === 'string' ? body.variables : '{}';
  const [selectedPaths, setSelectedPaths] = useState(EMPTY_SELECTED_PATHS);
  const [selectedArgPaths, setSelectedArgPaths] = useState(EMPTY_SELECTED_PATHS);
  const [argValues, setArgValues] = useState({});
  const queryEditedManuallyRef = useRef(false);
  const lastSchemaRef = useRef(null);
  const queryRef = useRef(query);
  queryRef.current = query;

  const restoreExplorerFromQuery = useCallback((queryText, schemaData) => {
    if (!schemaData) {
      setSelectedPaths(new Set());
      setSelectedArgPaths(new Set());
      setArgValues({});
      queryEditedManuallyRef.current = !!String(queryText ?? '').trim();
      return;
    }

    const trimmed = String(queryText ?? '').trim();
    if (!trimmed) {
      setSelectedPaths(new Set());
      setSelectedArgPaths(new Set());
      setArgValues({});
      queryEditedManuallyRef.current = false;
      return;
    }

    const {
      selectedPaths: restoredPaths,
      argValues: restoredArgs,
      selectedArgPaths: restoredArgPaths,
    } = parseQueryToSelectedPaths(trimmed, schemaData);
    setSelectedPaths(restoredPaths);
    setSelectedArgPaths(restoredArgPaths);
    setArgValues(restoredArgs);
    queryEditedManuallyRef.current = true;
  }, []);

  const handleSelectedPathsChange = useCallback((updater) => {
    queryEditedManuallyRef.current = false;
    setSelectedPaths(updater);
  }, []);

  const handleArgToggle = useCallback((path) => {
    queryEditedManuallyRef.current = false;
    setSelectedArgPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
        setArgValues((current) => {
          if (!(path in current)) return current;
          const { [path]: _removed, ...rest } = current;
          return rest;
        });
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleArgValueChange = useCallback((path, value) => {
    queryEditedManuallyRef.current = false;
    setArgValues((prev) => ({ ...prev, [path]: value }));
  }, []);

  const handleQueryChange = useCallback(
    (newQuery) => {
      queryEditedManuallyRef.current = true;
      onChange?.({ ...body, query: newQuery });
    },
    [body, onChange]
  );

  const handleVariablesChange = useCallback(
    (newVars) => onChange?.({ ...body, variables: newVars }),
    [body, onChange]
  );

  useEffect(() => {
    if (schema === lastSchemaRef.current) return;
    lastSchemaRef.current = schema;
    restoreExplorerFromQuery(queryRef.current, schema);
  }, [schema, restoreExplorerFromQuery]);

  useEffect(() => {
    if (queryEditedManuallyRef.current) return;
    if (!selectedPaths?.size || !schema) return;
    const nextQuery = buildQueryFromSelectedPaths(selectedPaths, schema, argValues);
    if (nextQuery && nextQuery !== query) {
      onChange?.({ ...body, query: nextQuery });
    }
  }, [selectedPaths, argValues, schema]); // eslint-disable-line react-hooks/exhaustive-deps

  const variableProps = {
    selectedEnvironment,
    globalVariables,
    collectionVariables,
    folderVariables,
    workspaceVariables,
    onVariableUpdate,
    variableContext,
  };

  return (
    <div className="gql-editor-root">
      <div className="gql-editor-panels">
        <ResizableSplitter
          direction="horizontal"
          initialSplit={30}
          minSize={18}
          maxSize={45}
          className="h-full"
        >
          <GraphQLSchemaExplorer
            schema={schema}
            schemaStatus={schemaStatus}
            schemaError={schemaError}
            selectedPaths={selectedPaths}
            onSelectedPathsChange={handleSelectedPathsChange}
            selectedArgPaths={selectedArgPaths}
            onArgToggle={handleArgToggle}
            argValues={argValues}
            onArgValueChange={handleArgValueChange}
          />

          <ResizableSplitter
            direction="horizontal"
            initialSplit={62}
            minSize={25}
            maxSize={80}
            className="h-full"
          >
            <div className="gql-editor-panel gql-editor-panel--query">
              <div className="gql-editor-panel__label">Query</div>
              <div className="gql-editor-panel__body">
                <CodeEditor
                  hideChrome
                  language="graphql"
                  value={query}
                  onChange={handleQueryChange}
                  placeholder="# Select fields from the schema or write your query"
                  height="100%"
                  splitLayoutTick={splitLayoutTick}
                  {...variableProps}
                />
              </div>
            </div>

            <div className="gql-editor-panel gql-editor-panel--variables">
              <div className="gql-editor-panel__label">Variables</div>
              <div className="gql-editor-panel__body">
                <CodeEditor
                  hideChrome
                  language="json"
                  value={variables}
                  onChange={handleVariablesChange}
                  placeholder="{}"
                  height="100%"
                  splitLayoutTick={splitLayoutTick}
                  {...variableProps}
                />
              </div>
            </div>
          </ResizableSplitter>
        </ResizableSplitter>
      </div>
    </div>
  );
};

export default GraphQLBodyEditor;
